import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import {
  makeStyles,
  useTheme,
  Typography,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Tabs,
  Tab,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SearchIcon from '@material-ui/icons/Search';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Page, Content } from '@backstage/core-components';
import { useApi, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { useLocation } from 'react-router-dom';
import { useHarnessData, HarnessTrackedProject } from './HarnessContext';

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';
  const palette = {
    bgApp: isDark ? '#1a1a1a' : '#f7f8fa',
    bgPanel: isDark ? '#222428' : '#ffffff',
    bgChiclet: isDark ? '#2e3034' : '#f0f2f5',
    border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    textPrimary: isDark ? '#e4e6e8' : '#111318',
    textSecondary: isDark ? '#8c9196' : '#6e7479',
    textLight: isDark ? '#6e7479' : '#8c9196',
    info: isDark ? '#4da3ff' : '#0078d4',
    warning: '#ffb366',
    error: '#ff4d4d',
  };

  return {
    pageRoot: {},
    tabsWrapper: {
      '& .MuiTabs-root': {
        minHeight: 40,
        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
        borderRadius: 10,
        padding: 4,
        marginBottom: 24,
      },
      '& .MuiTabs-indicator': {
        height: '100%',
        borderRadius: 8,
        background: isDark
          ? 'rgba(77,163,255,0.1)'
          : 'rgba(0,120,212,0.06)',
        zIndex: 0,
      },
      '& .MuiTab-root': {
        textTransform: 'none' as const,
        fontWeight: 500,
        fontSize: '0.84rem',
        letterSpacing: '-0.01em',
        minHeight: 34,
        borderRadius: 8,
        zIndex: 1,
        color: palette.textSecondary,
        transition: 'color 0.18s cubic-bezier(0.2, 0, 0, 1)',
        '&.Mui-selected': {
          color: palette.info,
          fontWeight: 600,
        },
      },
      '& .MuiTabs-flexContainer': {
        gap: 2,
      },
    },
    toolbar: {
      display: 'flex',
      gap: theme.spacing(2),
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      marginBottom: theme.spacing(3),
    },
    formControl: {
      minWidth: 200,
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
        transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
        },
        '&.Mui-focused': {
          background: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
          boxShadow: `0 0 0 3px rgba(77,163,255,0.15)`,
        },
      },
    },
    statusChip: {
      fontWeight: 600,
      fontSize: '0.73rem',
      borderRadius: 20,
      padding: '2px 4px',
      letterSpacing: '0.02em',
      boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.3)' : '0 1px 2px rgba(0,0,0,0.06)',
    },
    tableContainer: {
      borderRadius: 14,
      border: `1px solid ${palette.border}`,
      boxShadow: 'none',
      overflow: 'hidden',
      background: palette.bgPanel,
      '& .MuiTableCell-head': {
        fontWeight: 600,
        fontSize: '0.69rem',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: palette.textLight,
        background: isDark ? '#1e2024' : '#f7f8fa',
        borderBottom: `1px solid ${palette.border}`,
        padding: '12px 20px',
      },
      '& .MuiTableCell-body': {
        borderBottom: `1px solid ${palette.border}`,
        padding: '13px 20px',
        fontSize: '0.84rem',
        lineHeight: 1.6,
        color: palette.textPrimary,
      },
      '& .MuiTableRow-root': {
        '&:hover': {
          background: isDark
            ? 'rgba(255,255,255,0.02)'
            : 'rgba(0,0,0,0.015)',
        },
        '&:last-child .MuiTableCell-body': {
          borderBottom: 'none',
        },
      },
    },
    pipelineName: {
      fontWeight: 600,
      color: palette.textPrimary,
      letterSpacing: '-0.01em',
    },
    emptyState: {
      textAlign: 'center' as const,
      padding: theme.spacing(10, 4),
      color: palette.textSecondary,
      '& h6, & p': {
        maxWidth: 400,
        margin: '0 auto',
      },
      '& svg': {
        fontSize: 56,
        color: palette.textLight,
        marginBottom: theme.spacing(2),
        opacity: 0.6,
      },
    },
    searchField: {
      minWidth: 280,
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        background: isDark ? 'rgba(255,255,255,0.03)' : 'transparent',
        transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
        '& fieldset': {
          borderColor: palette.border,
        },
        '&:hover': {
          '& fieldset': {
            borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
          },
        },
        '&.Mui-focused': {
          boxShadow: `0 0 0 3px ${isDark ? 'rgba(77,163,255,0.15)' : 'rgba(0,120,212,0.1)'}`,
          '& fieldset': {
            borderColor: `${palette.info} !important`,
            borderWidth: '1.5px',
          },
        },
      },
    },
    filterBar: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      flexWrap: 'wrap' as const,
      marginBottom: theme.spacing(3),
      padding: theme.spacing(1, 0),
    },
    statCard: {
      flex: '1 1 160px',
      minWidth: 150,
      maxWidth: 240,
      background: palette.bgPanel,
      border: `1px solid ${palette.border}`,
      borderRadius: 12,
      padding: theme.spacing(2.5, 3),
      position: 'relative' as const,
      overflow: 'hidden',
      transition: 'border-color 0.18s ease, transform 0.18s ease',
    },
    statCardClickable: {
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-1px)',
        borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
      },
    },
    statCardActive: {
      borderColor: `${palette.info} !important`,
      background: isDark
        ? 'rgba(77,163,255,0.06) !important'
        : 'rgba(0,120,212,0.04) !important',
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.1,
      color: palette.textPrimary,
      letterSpacing: '-0.03em',
      fontFeatureSettings: '"tnum"',
    },
    statLabel: {
      fontSize: '0.68rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.06em',
      color: palette.textSecondary,
      marginTop: theme.spacing(0.75),
      fontWeight: 500,
    },
    overviewCard: {
      height: '100%',
      borderRadius: 16,
      border: `1px solid ${palette.border}`,
      boxShadow: isDark
        ? '0 2px 12px rgba(0,0,0,0.2)'
        : '0 2px 12px rgba(0,0,0,0.03)',
      background: palette.bgPanel,
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s ease',
      '&:hover': {
        borderColor: palette.info,
        transform: 'translateY(-3px)',
        boxShadow: isDark
          ? '0 12px 40px rgba(0,0,0,0.35), 0 4px 12px rgba(77,163,255,0.08)'
          : '0 12px 40px rgba(0,0,0,0.08), 0 4px 12px rgba(77,163,255,0.04)',
      },
    },
    overviewCardContent: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: theme.spacing(1.25),
      height: '100%',
      padding: theme.spacing(0.5),
    },
    overviewProjectName: {
      fontWeight: 700,
      fontSize: '1.05rem',
      color: palette.textPrimary,
      letterSpacing: '-0.01em',
    },
    overviewOrgLabel: {
      fontSize: '0.72rem',
      color: palette.textSecondary,
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
    overviewExecName: {
      fontSize: '0.82rem',
      color: palette.textSecondary,
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    orgCard: {
      height: '100%',
      borderRadius: 16,
      border: `1px solid ${palette.border}`,
      boxShadow: isDark
        ? '0 2px 12px rgba(0,0,0,0.2)'
        : '0 2px 12px rgba(0,0,0,0.03)',
      background: palette.bgPanel,
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s ease',
      '&:hover': {
        borderColor: palette.info,
        transform: 'translateY(-2px)',
        boxShadow: isDark
          ? '0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(77,163,255,0.08)'
          : '0 8px 32px rgba(0,0,0,0.06), 0 2px 8px rgba(77,163,255,0.04)',
      },
    },
    orgIdentifier: {
      fontSize: '0.72rem',
      color: palette.textLight,
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontWeight: 400,
      letterSpacing: '0.02em',
      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      padding: '2px 8px',
      borderRadius: 6,
      display: 'inline-block',
    },
    sectionTitle: {
      fontWeight: 700,
      fontSize: '1.15rem',
      marginBottom: theme.spacing(2.5),
      color: palette.textPrimary,
      letterSpacing: '-0.02em',
    },
  };
});

const STATUS_STYLES: Record<string, { background: string; color: string }> = {
  Success:  { background: '#1b5e20', color: '#a5d6a7' },
  Failed:   { background: '#b71c1c', color: '#ef9a9a' },
  Running:  { background: '#0d47a1', color: '#90caf9' },
  Aborted:  { background: '#424242', color: '#bdbdbd' },
  Expired:  { background: '#4a148c', color: '#ce93d8' },
};

function formatDuration(ms: number): string {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

function formatDate(ts: number): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

interface Project { identifier: string; name: string; }
interface Execution {
  planExecutionId: string;
  name: string;
  pipelineIdentifier: string;
  status: string;
  startTs: number;
  endTs: number;
  triggerType: string;
  moduleInfo?: { ci?: { ciExecutionInfoDTO?: { branch?: { name?: string } } } };
}

export const HarnessPipelinesPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const location = useLocation();

  // Orgs, userOrgs, trackedProjects pre-fetched at app start via HarnessProvider
  const { orgs, userOrgs: _userOrgs, trackedProjects, toggleTrack, accountId, orgsLoading } = useHarnessData();

  const qp = new URLSearchParams(location.search);
  const defaultOrg = qp.get('org') ?? '';
  const defaultProject = qp.get('project') ?? '';

  const urlDefaultsApplied = useRef(false);
  const selectFirstProjectRef = useRef(false);

  // ── Main tab: 0 = My Subscriptions, 1 = Subscribe ──
  const [activeTab, setActiveTab] = useState<0 | 1>(0);

  const [_projects, setProjects] = useState<Project[]>([]);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [selectedOrg, setSelectedOrg] = useState(defaultOrg);
  const [selectedProject, setSelectedProject] = useState(defaultProject);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [_showAllOrgs, _setShowAllOrgs] = useState(false);
  const [showOrgBrowse, setShowOrgBrowse] = useState(false);

  interface OverviewItem {
    orgId: string;
    projectId: string;
    projectName: string;
    lastExec?: { status: string; pipelineName: string; startTs: number; planExecutionId?: string; pipelineIdentifier?: string };
  }
  const [overviewStats, setOverviewStats] = useState<OverviewItem[]>([]);
  const [overviewFilter, setOverviewFilter] = useState<'all' | 'subscribed' | 'passed' | 'failed'>('all');
  const [overviewSearch, setOverviewSearch] = useState('');
  const [overviewLoading, setOverviewLoading] = useState(false);
  const PAGE_SIZE = 20;

  // ── Subscribe tab state ──────────────────────────────────────────────────
  const [subFilterBy, setSubFilterBy] = useState<'all' | 'orgs' | 'projects'>('all');
  const [subSearch, setSubSearch] = useState('');
  const [subExpandedOrgs, setSubExpandedOrgs] = useState<Set<string>>(new Set());
  const [subExpandedProjects, setSubExpandedProjects] = useState<Set<string>>(new Set());
  const [subOrgProjectsLoading, setSubOrgProjectsLoading] = useState<Record<string, boolean>>({});
  const [subProjectPipelinesLoading, setSubProjectPipelinesLoading] = useState<Record<string, boolean>>({});
  const [subProjectPipelinesErrors, setSubProjectPipelinesErrors] = useState<Record<string, boolean>>({});
  const [subOrgBellLoading, setSubOrgBellLoading] = useState<Record<string, boolean>>({});
  const [subProjectBellLoading, setSubProjectBellLoading] = useState<Record<string, boolean>>({});
  const [allSubProjects, setAllSubProjects] = useState<{ identifier: string; name: string; orgId: string; orgName: string }[]>([]);
  const [allSubProjectsLoading, setAllSubProjectsLoading] = useState(false);
  const [subProjectsPage, setSubProjectsPage] = useState(0);
  const subOrgProjectsCacheRef = useRef<Record<string, { identifier: string; name: string }[]>>({});
  const subProjectPipelinesCacheRef = useRef<Record<string, { id: string; name: string }[]>>({});

  const [watchedPipelines, setWatchedPipelines] = useState<Set<string>>(() => {
    const keys = new Set<string>();
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('harness-pipeline-sub:') && !k.endsWith(':name')) keys.add(k.slice('harness-pipeline-sub:'.length));
    }
    return keys;
  });

  const isPipelineWatched = useCallback((orgId: string, projectId: string, pipelineId: string) =>
    watchedPipelines.has(`${orgId}/${projectId}/${pipelineId}`), [watchedPipelines]);

  const togglePipelineWatch = useCallback((orgId: string, projectId: string, pipelineId: string, pipeName: string) => {
    const subKey = `${orgId}/${projectId}/${pipelineId}`;
    const lsKey = `harness-pipeline-sub:${subKey}`;
    setWatchedPipelines(prev => {
      const next = new Set(prev);
      if (next.has(subKey)) {
        next.delete(subKey);
        localStorage.removeItem(lsKey);
        localStorage.removeItem(`${lsKey}:name`);
      } else {
        next.add(subKey);
        localStorage.setItem(lsKey, '1');
        localStorage.setItem(`${lsKey}:name`, pipeName);
      }
      return next;
    });
  }, []);

  const renderBellIcon = (active: boolean, partial?: boolean) => {
    if (active) return <NotificationsActiveIcon fontSize="small" style={{ color: '#f5a623' }} />;
    if (partial) return <NotificationsActiveIcon fontSize="small" style={{ color: '#90caf9' }} />;
    return <NotificationsNoneIcon fontSize="small" />;
  };

  const proxyBase = useCallback(async () => {
    const base = await discoveryApi.getBaseUrl('proxy');
    return `${base}/harness`;
  }, [discoveryApi]);

  // ── Subscribe tab callbacks ──────────────────────────────────────────────

  const fetchSubOrgProjectsCached = useCallback(async (orgId: string): Promise<{ identifier: string; name: string }[]> => {
    if (subOrgProjectsCacheRef.current[orgId]) return subOrgProjectsCacheRef.current[orgId];
    try {
      const base = await proxyBase();
      const resp = await fetchApi.fetch(`${base}/ng/api/projects?accountIdentifier=${accountId}&orgIdentifier=${orgId}&pageSize=200`);
      if (!resp.ok) return [];
      const data = await resp.json();
      const projects = (data.data?.content ?? []).map((p: any) => ({ identifier: p.project.identifier, name: p.project.name }));
      subOrgProjectsCacheRef.current[orgId] = projects;
      return projects;
    } catch { return []; }
  }, [accountId, fetchApi, proxyBase]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSubProjectPipelines = useCallback(async (orgId: string, projectId: string) => {
    const key = `${orgId}/${projectId}`;
    if (subProjectPipelinesCacheRef.current[key]) return;
    setSubProjectPipelinesLoading(prev => ({ ...prev, [key]: true }));
    setSubProjectPipelinesErrors(prev => ({ ...prev, [key]: false }));
    try {
      const base = await proxyBase();
      const resp = await fetchApi.fetch(
        `${base}/pipeline/api/pipelines/list?accountIdentifier=${accountId}&orgIdentifier=${orgId}&projectIdentifier=${projectId}&page=0&size=200`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ filterType: 'PipelineSetup' }) },
      );
      if (resp.ok) {
        const data = await resp.json();
        const items: any[] = data.data?.content ?? (Array.isArray(data.data) ? data.data : (data.content ?? []));
        subProjectPipelinesCacheRef.current[key] = items.map((p: any) => ({ id: p.identifier, name: p.name || p.identifier }));
      } else {
        setSubProjectPipelinesErrors(prev => ({ ...prev, [key]: true }));
        subProjectPipelinesCacheRef.current[key] = [];
      }
    } catch {
      setSubProjectPipelinesErrors(prev => ({ ...prev, [key]: true }));
      subProjectPipelinesCacheRef.current[key] = [];
    } finally {
      setSubProjectPipelinesLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [accountId, fetchApi, proxyBase]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSubOrgExpand = useCallback((orgId: string) => {
    const alreadyOpen = subExpandedOrgs.has(orgId);
    setSubExpandedOrgs(prev => { const n = new Set(prev); n.has(orgId) ? n.delete(orgId) : n.add(orgId); return n; });
    if (!alreadyOpen) {
      setSubOrgProjectsLoading(prev => ({ ...prev, [orgId]: true }));
      fetchSubOrgProjectsCached(orgId).finally(() => setSubOrgProjectsLoading(prev => ({ ...prev, [orgId]: false })));
    }
  }, [subExpandedOrgs, fetchSubOrgProjectsCached]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSubProjectExpand = useCallback((orgId: string, projectId: string) => {
    const key = `${orgId}/${projectId}`;
    const alreadyOpen = subExpandedProjects.has(key);
    setSubExpandedProjects(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
    if (!alreadyOpen) fetchSubProjectPipelines(orgId, projectId);
  }, [subExpandedProjects, fetchSubProjectPipelines]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOrgTracked = useCallback((orgId: string): boolean | 'partial' => {
    const projects = subOrgProjectsCacheRef.current[orgId];
    const trackedForOrg = trackedProjects.filter(t => t.orgId === orgId);
    if (!projects) return trackedForOrg.length > 0 ? 'partial' : false;
    if (projects.length === 0) return false;
    const trackedIds = new Set(trackedForOrg.map(t => t.projectId));
    const allTracked = projects.every(p => trackedIds.has(p.identifier));
    const anyTracked = projects.some(p => trackedIds.has(p.identifier));
    if (allTracked) return true;
    if (anyTracked) return 'partial';
    return false;
  }, [trackedProjects]);

  const isProjectTracked = useCallback((orgId: string, projectId: string) =>
    trackedProjects.some(t => t.orgId === orgId && t.projectId === projectId), [trackedProjects]);

  const toggleSubOrgTrack = useCallback(async (orgId: string) => {
    setSubOrgBellLoading(prev => ({ ...prev, [orgId]: true }));
    try {
      const projects = await fetchSubOrgProjectsCached(orgId);
      const trackedIds = new Set(trackedProjects.filter(t => t.orgId === orgId).map(t => t.projectId));
      const allTracked = projects.length > 0 && projects.every(p => trackedIds.has(p.identifier));
      for (const p of projects) {
        const currently = trackedIds.has(p.identifier);
        if (allTracked && currently) await toggleTrack(orgId, p.identifier, p.name);
        else if (!allTracked && !currently) await toggleTrack(orgId, p.identifier, p.name);
        const pipelines = subProjectPipelinesCacheRef.current[`${orgId}/${p.identifier}`];
        if (pipelines) {
          for (const pipe of pipelines) {
            const subKey = `${orgId}/${p.identifier}/${pipe.id}`;
            const pipeWatched = localStorage.getItem(`harness-pipeline-sub:${subKey}`) !== null;
            if (!allTracked && !pipeWatched) togglePipelineWatch(orgId, p.identifier, pipe.id, pipe.name);
            else if (allTracked && pipeWatched) togglePipelineWatch(orgId, p.identifier, pipe.id, pipe.name);
          }
        }
      }
    } catch { /* ignore */ } finally {
      setSubOrgBellLoading(prev => ({ ...prev, [orgId]: false }));
    }
  }, [fetchSubOrgProjectsCached, trackedProjects, toggleTrack, togglePipelineWatch]);

  const toggleSubProjectWithPipelines = useCallback(async (orgId: string, projectId: string, projectName: string) => {
    const projKey = `${orgId}/${projectId}`;
    setSubProjectBellLoading(prev => ({ ...prev, [projKey]: true }));
    try {
      const currentlyTracked = trackedProjects.some(t => t.orgId === orgId && t.projectId === projectId);
      await fetchSubProjectPipelines(orgId, projectId);
      const pipelines = subProjectPipelinesCacheRef.current[projKey] ?? [];
      await toggleTrack(orgId, projectId, projectName);
      for (const pipe of pipelines) {
        const subKey = `${orgId}/${projectId}/${pipe.id}`;
        const pipeWatched = localStorage.getItem(`harness-pipeline-sub:${subKey}`) !== null;
        if (!currentlyTracked && !pipeWatched) togglePipelineWatch(orgId, projectId, pipe.id, pipe.name);
        else if (currentlyTracked && pipeWatched) togglePipelineWatch(orgId, projectId, pipe.id, pipe.name);
      }
    } catch { /* ignore */ } finally {
      setSubProjectBellLoading(prev => ({ ...prev, [`${orgId}/${projectId}`]: false }));
    }
  }, [fetchSubProjectPipelines, trackedProjects, toggleTrack, togglePipelineWatch]);

  // Load all projects when filterBy=projects
  useEffect(() => {
    if (activeTab !== 1 || subFilterBy !== 'projects') return;
    if (allSubProjects.length > 0) return;
    let cancelled = false;
    (async () => {
      setAllSubProjectsLoading(true);
      try {
        const all: { identifier: string; name: string; orgId: string; orgName: string }[] = [];
        await Promise.all(orgs.map(async org => {
          const projects = await fetchSubOrgProjectsCached(org.identifier);
          for (const p of projects) all.push({ identifier: p.identifier, name: p.name, orgId: org.identifier, orgName: org.name });
        }));
        if (!cancelled) setAllSubProjects(all);
      } finally {
        if (!cancelled) setAllSubProjectsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, subFilterBy, orgs, allSubProjects.length, fetchSubOrgProjectsCached]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset Subscribe tab search on filterBy change
  useEffect(() => { setSubSearch(''); setSubProjectsPage(0); }, [subFilterBy]);

  // Pipeline subscriptions from localStorage (harness-pipeline-sub:{orgId}/{projectId}/{pipelineId})
  const [pipeSubbedProjectKeys, setPipeSubbedProjectKeys] = useState(() => {
    const map = new Map<string, string>(); // key: "orgId/projectId", val: orgId
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('harness-pipeline-sub:') && !k.endsWith(':name')) {
        const parts = k.slice('harness-pipeline-sub:'.length).split('/');
        if (parts.length >= 3) map.set(`${parts[0]}/${parts[1]}`, parts[0]);
      }
    }
    return map;
  });

  // Untrack: removes from DB + clears localStorage pipeline subs for that project
  const handleUntrack = useCallback((orgId: string, projectId: string, projectName?: string) => {
    // Remove all localStorage keys for this org/project
    const prefix = `harness-pipeline-sub:${orgId}/${projectId}/`;
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith(prefix) || k === `${prefix}name`)) keysToRemove.push(k);
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    // Update local state so card disappears immediately
    setPipeSubbedProjectKeys(prev => {
      const next = new Map(prev);
      next.delete(`${orgId}/${projectId}`);
      return next;
    });
    // Also remove from DB-tracked list
    toggleTrack(orgId, projectId, projectName);
  }, [toggleTrack]);

  // Combined projects for overview: DB-tracked + pipeline-subscribed
  const allTrackedForOverview = useMemo(() => {
    const result: HarnessTrackedProject[] = [...trackedProjects];
    const trackedKeys = new Set(trackedProjects.map(t => `${t.orgId}/${t.projectId}`));
    for (const [key, orgId] of pipeSubbedProjectKeys) {
      if (!trackedKeys.has(key)) {
        const projectId = key.slice(orgId.length + 1);
        result.push({ orgId, projectId, projectName: projectId });
      }
    }
    return result;
  }, [trackedProjects, pipeSubbedProjectKeys]);

  const loadOverview = useCallback(async () => {
    if (allTrackedForOverview.length === 0) { setOverviewStats([]); return; }
    setOverviewLoading(true);
    try {
      const base = await proxyBase();
      const results = await Promise.allSettled(
        allTrackedForOverview.map(async ({ orgId, projectId, projectName }) => {
          try {
            const resp = await fetchApi.fetch(
              `${base}/pipeline/api/pipelines/execution/summary?accountIdentifier=${accountId}&orgIdentifier=${orgId}&projectIdentifier=${projectId}&page=0&size=1`,
              { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ filterType: 'PipelineExecution' }) },
            );
            const data = resp.ok ? await resp.json() : {};
            const exec = data.data?.content?.[0];
            return {
              orgId,
              projectId,
              projectName: projectName ?? projectId,
              lastExec: exec ? { status: exec.status, pipelineName: exec.name || exec.pipelineIdentifier, startTs: exec.startTs, planExecutionId: exec.planExecutionId, pipelineIdentifier: exec.pipelineIdentifier } : undefined,
            };
          } catch { return { orgId, projectId, projectName: projectName ?? projectId }; }
        }),
      );
      setOverviewStats(results.filter(r => r.status === 'fulfilled').map(r => (r as PromiseFulfilledResult<OverviewItem>).value));
    } catch { /* ignore */ } finally {
      setOverviewLoading(false);
    }
  }, [allTrackedForOverview, accountId, fetchApi, proxyBase]); // eslint-disable-line

  // Reload overview when landing on the no-project state
  useEffect(() => {
    if (!selectedProject) loadOverview();
  }, [selectedProject, trackedProjects]); // eslint-disable-line

  // Load projects when org changes
  useEffect(() => {
    if (!selectedOrg) { setProjects([]); setSelectedProject(''); return undefined; }
    (async () => {
      try {
        const base = await proxyBase();
        const resp = await fetchApi.fetch(
          `${base}/ng/api/projects?accountIdentifier=${accountId}&orgIdentifier=${selectedOrg}&pageSize=200`,
        );
        if (!resp.ok) throw new Error(`${resp.status}`);
        const data = await resp.json();
        const items: Project[] = (data.data?.content ?? []).map((p: any) => ({
          identifier: p.project.identifier,
          name: p.project.name,
        }));
        setProjects(items);
        // Auto-select only when navigated here with a URL ?project= query param
        if (!urlDefaultsApplied.current && defaultProject) {
          setSelectedProject(defaultProject);
          urlDefaultsApplied.current = true;
        } else if (selectFirstProjectRef.current && items.length > 0) {
          // Org card was clicked — jump straight to first project
          selectFirstProjectRef.current = false;
          setSelectedProject(items[0].identifier);
        } else if (!defaultProject) {
          // Clear project selection when org changes so At a Glance stays visible
          setSelectedProject('');
          setExecutions([]);
        }
      } catch (e: any) {
        setError(`Failed to load projects: ${e.message}`);
      }
    })();
    return undefined;
  }, [selectedOrg, accountId, defaultProject, fetchApi, proxyBase]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Load executions when project/page/search changes
  const loadExecutions = useCallback(async (pageNum: number, searchTerm = '') => {
    if (!selectedProject) { setExecutions([]); return; }
    setLoading(true);
    setError('');
    try {
      const base = await proxyBase();
      const qs = new URLSearchParams({
        accountIdentifier: accountId,
        orgIdentifier: selectedOrg,
        projectIdentifier: selectedProject,
        page: String(pageNum),
        size: String(PAGE_SIZE),
        ...(searchTerm ? { searchTerm } : {}),
      }).toString();
      const resp = await fetchApi.fetch(
        `${base}/pipeline/api/pipelines/execution/summary?${qs}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ filterType: 'PipelineExecution' }) },
      );
      if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      setExecutions(data.data?.content ?? []);
      setTotalItems(data.data?.totalItems ?? 0);
    } catch (e: any) {
      setError(`Failed to load executions: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, selectedOrg, accountId, fetchApi, proxyBase]);

  useEffect(() => {
    setPage(0);
    setSearch('');
    setDebouncedSearch('');
    loadExecutions(0, '');
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(0);
    loadExecutions(0, debouncedSearch);
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const harnessExecutionUrl = (exec: Execution) =>
    `https://app.harness.io/ng/account/${accountId}/all/orgs/${selectedOrg}/projects/${selectedProject}/pipelines/${exec.pipelineIdentifier}/executions/${exec.planExecutionId}/pipeline`;

  return (
    <Page themeId="tool">
      <Content>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v as 0 | 1)} indicatorColor="primary" textColor="primary" style={{ marginBottom: 24 }}>
          <Tab label={`My Subscriptions${allTrackedForOverview.length > 0 ? ` (${allTrackedForOverview.length})` : ''}`} />
          <Tab label="Subscribe" />
        </Tabs>

        {activeTab === 0 && <>
          {!accountId && (
          <Box mb={2}>
            <Typography color="error">
              Missing <code>harness.accountId</code> in app-config.yaml and <code>HARNESS_TOKEN</code> env var.
            </Typography>
          </Box>
        )}

        <Box className={classes.toolbar}>
          {(selectedProject || showOrgBrowse) && (
            <Tooltip title="Back to overview">
              <IconButton
                size="small"
                onClick={() => {
                  if (showOrgBrowse) {
                    setShowOrgBrowse(false);
                    setSearch('');
                  } else {
                    setSelectedProject('');
                    setExecutions([]);
                  }
                }}
                style={{ marginRight: 4 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {(selectedProject || showOrgBrowse) && (
            <TextField
              variant="outlined"
              size="small"
              placeholder="Filter by name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {selectedProject && (
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => loadExecutions(page, debouncedSearch)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {loading && <CircularProgress size={20} />}
        </Box>

        {error && (
          <Box mb={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {selectedProject && !loading && (
          <>
            <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pipeline</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box className={classes.emptyState}>No executions found</Box>
                      </TableCell>
                    </TableRow>
                  ) : executions.map(exec => {
                    const style = STATUS_STYLES[exec.status] ?? { background: '#424242', color: '#bdbdbd' };
                    const branch = exec.moduleInfo?.ci?.ciExecutionInfoDTO?.branch?.name ?? '—';
                    return (
                      <TableRow key={exec.planExecutionId} hover>
                        <TableCell className={classes.pipelineName}>{exec.name || exec.pipelineIdentifier}</TableCell>
                        <TableCell>
                          <Chip
                            label={exec.status}
                            size="small"
                            className={classes.statusChip}
                            style={style}
                          />
                        </TableCell>
                        <TableCell>{exec.triggerType ?? '—'}</TableCell>
                        <TableCell>{branch}</TableCell>
                        <TableCell>{formatDate(exec.startTs)}</TableCell>
                        <TableCell>{formatDuration(exec.endTs - exec.startTs)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Open in Harness">
                            <IconButton
                              size="small"
                              href={harnessExecutionUrl(exec)}
                              target="_blank"
                              rel="noopener noreferrer"
                              component="a"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_e, newPage) => {
                setPage(newPage);
                loadExecutions(newPage, debouncedSearch);
              }}
            />
          </>
        )}

        {showOrgBrowse && (
          <Box>
            <Typography variant="h6" className={classes.sectionTitle}>
              All Organizations ({orgs.length})
            </Typography>
            <Grid container spacing={2}>
              {orgs
                .filter(o =>
                  !search
                    ? true
                    : o.name.toLowerCase().includes(search.toLowerCase()) ||
                      o.identifier.toLowerCase().includes(search.toLowerCase())
                )
                .map(org => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={org.identifier}>
                    <Card variant="outlined" className={classes.orgCard}>
                      <CardActionArea
                        onClick={() => {
                          setSelectedOrg(org.identifier);
                          setShowOrgBrowse(false);
                          setSearch('');
                        }}
                        style={{ height: '100%' }}
                      >
                        <CardContent className={classes.overviewCardContent}>
                          <Typography className={classes.overviewProjectName} noWrap>
                            {org.name}
                          </Typography>
                          <Typography className={classes.orgIdentifier}>{org.identifier}</Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
            </Grid>
            {orgs.filter(o =>
              !search
                ? false
                : !o.name.toLowerCase().includes(search.toLowerCase()) &&
                  !o.identifier.toLowerCase().includes(search.toLowerCase())
            ).length === orgs.length && (
              <Box className={classes.emptyState}>No organizations match "{search}"</Box>
            )}
          </Box>
        )}

        {!selectedProject && !showOrgBrowse && !loading && (
          <Box>
            {/* ── At a Glance ─────────────────────────────────── */}
            <Typography variant="h6" className={classes.sectionTitle}>At a Glance</Typography>
            <Box display="flex" flexWrap="wrap" style={{ gap: 16 }} mb={4}>
              <Box className={classes.statCard}>
                <Typography className={classes.statValue}>{orgs.length}</Typography>
                <Typography className={classes.statLabel}>Organizations</Typography>
              </Box>
              <Box
                className={`${classes.statCard} ${classes.statCardClickable} ${overviewFilter === 'subscribed' ? classes.statCardActive : ''}`}
                onClick={() => setOverviewFilter(f => f === 'subscribed' ? 'all' : 'subscribed')}
              >
                <Typography className={classes.statValue}>{allTrackedForOverview.length}</Typography>
                <Typography className={classes.statLabel}>Subscribed Projects{overviewFilter === 'subscribed' ? ' ✓' : ''}</Typography>
              </Box>
              <Box
                className={`${classes.statCard} ${classes.statCardClickable} ${overviewFilter === 'passed' ? classes.statCardActive : ''}`}
                onClick={() => setOverviewFilter(f => f === 'passed' ? 'all' : 'passed')}
              >
                <Typography className={classes.statValue}>
                  {overviewStats.filter(s => s.lastExec?.status === 'Success').length}
                </Typography>
                <Typography className={classes.statLabel}>Passed (last run){overviewFilter === 'passed' ? ' ✓' : ''}</Typography>
              </Box>
              <Box
                className={`${classes.statCard} ${classes.statCardClickable} ${overviewFilter === 'failed' ? classes.statCardActive : ''}`}
                onClick={() => setOverviewFilter(f => f === 'failed' ? 'all' : 'failed')}
              >
                <Typography className={classes.statValue}>
                  {overviewStats.filter(s => s.lastExec && s.lastExec.status !== 'Success').length}
                </Typography>
                <Typography className={classes.statLabel}>Failed / Other{overviewFilter === 'failed' ? ' ✓' : ''}</Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Filter by name…"
                value={overviewSearch}
                onChange={e => setOverviewSearch(e.target.value)}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {overviewLoading && <CircularProgress size={20} />}
            </Box>

            {/* ── Tracked projects grid ───────────────────────── */}
            {allTrackedForOverview.length > 0 && (
              <>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Tracked Projects — Recent Activity
                  {overviewLoading && <CircularProgress size={14} style={{ marginLeft: 8, verticalAlign: 'middle' }} />}
                </Typography>
                <Grid container spacing={2}>
                  {(overviewFilter === 'all' || overviewFilter === 'subscribed' ? overviewStats
                    : overviewFilter === 'passed' ? overviewStats.filter(s => s.lastExec?.status === 'Success')
                    : overviewStats.filter(s => s.lastExec && s.lastExec.status !== 'Success')
                  ).filter(item =>
                    !overviewSearch
                      ? true
                      : item.projectName.toLowerCase().includes(overviewSearch.toLowerCase()) ||
                        item.projectId.toLowerCase().includes(overviewSearch.toLowerCase()) ||
                        item.orgId.toLowerCase().includes(overviewSearch.toLowerCase())
                  ).map(item => {
                    const style = item.lastExec ? (STATUS_STYLES[item.lastExec.status] ?? STATUS_STYLES.Aborted) : undefined;
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={`${item.orgId}/${item.projectId}`}>
                        <Card variant="outlined" className={classes.overviewCard}>
                          <CardActionArea
                            component="div"
                            onClick={() => { setSelectedOrg(item.orgId); setSelectedProject(item.projectId); }}
                            style={{ height: '100%' }}
                          >
                            <CardContent className={classes.overviewCardContent}>
                              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                                <Typography className={classes.overviewProjectName} noWrap style={{ flex: 1 }}>
                                  {item.projectName}
                                </Typography>
                                <Box display="flex" alignItems="center" style={{ gap: 0 }}>
                                  {item.lastExec?.planExecutionId && item.lastExec?.pipelineIdentifier && (
                                    <Tooltip title="Open in Harness">
                                      <IconButton
                                        size="small"
                                        style={{ padding: 2 }}
                                        onClick={e => {
                                          e.stopPropagation();
                                          window.open(
                                            `https://app.harness.io/ng/account/${accountId}/all/orgs/${item.orgId}/projects/${item.projectId}/pipelines/${item.lastExec!.pipelineIdentifier}/executions/${item.lastExec!.planExecutionId}/pipeline`,
                                            '_blank',
                                          );
                                        }}
                                      >
                                        <OpenInNewIcon style={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Untrack project">
                                    <IconButton
                                      size="small"
                                      style={{ padding: 2 }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        handleUntrack(item.orgId, item.projectId, item.projectName);
                                      }}
                                    >
                                      <NotificationsOffIcon style={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Typography className={classes.overviewOrgLabel}>{item.orgId}</Typography>
                              {item.lastExec ? (
                                <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 4 }}>
                                  <Chip
                                    label={item.lastExec.status}
                                    size="small"
                                    className={classes.statusChip}
                                    style={style}
                                  />
                                  <Typography className={classes.overviewExecName}>
                                    {item.lastExec.pipelineName}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="textSecondary">No executions</Typography>
                              )}
                              {item.lastExec?.startTs ? (
                                <Typography variant="caption" color="textSecondary">
                                  {formatDate(item.lastExec.startTs)}
                                </Typography>
                              ) : null}
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}

            {allTrackedForOverview.length === 0 && !overviewLoading && (
              <Box className={classes.emptyState}>
                <Typography variant="body1" gutterBottom>No tracked projects yet.</Typography>
                <Typography variant="body2" color="textSecondary">
                  Select an organization and project above, then click the <NotificationsNoneIcon style={{ fontSize: 16, verticalAlign: 'middle' }} /> bell icon to start tracking it.
                </Typography>
              </Box>
            )}
          </Box>
        )}
        </>}

        {/* ── Tab 1: Subscribe ── */}
        {activeTab === 1 && <>
          {(orgsLoading || allSubProjectsLoading) && <LinearProgress style={{ marginBottom: 16 }} />}

          <Box className={classes.filterBar}>
            <Select
              value={subFilterBy}
              onChange={e => setSubFilterBy(e.target.value as 'all' | 'orgs' | 'projects')}
              variant="outlined"
              style={{ minWidth: 160, height: 40 }}
              inputProps={{ 'aria-label': 'Filter by' }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="orgs">Organizations</MenuItem>
              <MenuItem value="projects">Projects</MenuItem>
            </Select>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search…"
              value={subSearch}
              onChange={e => setSubSearch(e.target.value)}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => {
                subOrgProjectsCacheRef.current = {};
                subProjectPipelinesCacheRef.current = {};
                setSubExpandedOrgs(new Set());
                setSubExpandedProjects(new Set());
                setAllSubProjects([]);
              }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Hierarchical tree: orgs → projects → pipelines */}
          {(subFilterBy === 'all' || subFilterBy === 'orgs') && (
            <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
              <Table size="small">
                <colgroup>
                  <col style={{ width: 44 }} />
                  <col />
                  <col style={{ width: 200 }} />
                  <col style={{ width: 200 }} />
                  <col style={{ width: 120 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Name</TableCell>
                    <TableCell>Identifier</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell align="right">Subscribe</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ padding: 40 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : orgs.filter(o => !subSearch || o.name.toLowerCase().includes(subSearch.toLowerCase()) || o.identifier.toLowerCase().includes(subSearch.toLowerCase())).map(org => {
                    const isOrgOpen = subExpandedOrgs.has(org.identifier);
                    const orgState = isOrgTracked(org.identifier);
                    const bellLoading = !!subOrgBellLoading[org.identifier];
                    const projects = subOrgProjectsCacheRef.current[org.identifier];
                    const projLoading = !!subOrgProjectsLoading[org.identifier];
                    const trackedForOrg = trackedProjects.filter(t => t.orgId === org.identifier).length;
                    return (
                      <Fragment key={org.identifier}>
                        <TableRow hover>
                          <TableCell style={{ padding: '4px 8px' }}>
                            <IconButton size="small" onClick={() => toggleSubOrgExpand(org.identifier)}>
                              {isOrgOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell style={{ fontWeight: 600 }}>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                              <Chip label="Organization" size="small" style={{ background: 'rgba(144,202,249,0.15)', color: '#90caf9', fontSize: '0.68rem', height: 18 }} />
                              {org.name}
                            </Box>
                          </TableCell>
                          <TableCell style={{ fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)', fontSize: '0.82rem' }}>
                            {org.identifier}
                          </TableCell>
                          <TableCell style={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)', fontSize: '0.82rem' }}>
                            {projects ? `${projects.length} project${projects.length !== 1 ? 's' : ''}` : '—'}{trackedForOrg > 0 ? ` · ${trackedForOrg} subscribed` : ''}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={bellLoading ? 'Loading…' : orgState === true ? 'Unsubscribe all projects' : orgState === 'partial' ? 'Subscribe remaining projects' : 'Subscribe all projects'}>
                              <span>
                                <IconButton size="small" disabled={bellLoading} onClick={() => toggleSubOrgTrack(org.identifier)}>
                                  {bellLoading ? <CircularProgress size={16} /> : renderBellIcon(orgState === true, orgState === 'partial')}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        {isOrgOpen && projLoading && (
                          <TableRow>
                            <TableCell colSpan={5} style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                              <Box display="flex" justifyContent="center" p={1.5}><CircularProgress size={20} /></Box>
                            </TableCell>
                          </TableRow>
                        )}
                        {isOrgOpen && !projLoading && projects && projects.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} style={{ paddingLeft: 48, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.82rem', background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>No projects</TableCell>
                          </TableRow>
                        )}
                        {isOrgOpen && !projLoading && projects && projects.map(project => {
                          const expandKey = `${org.identifier}/${project.identifier}`;
                          const tracked = isProjectTracked(org.identifier, project.identifier);
                          const isProjOpen = subExpandedProjects.has(expandKey);
                          const pLoading = !!subProjectBellLoading[expandKey];
                          const pipelines = subProjectPipelinesCacheRef.current[expandKey];
                          const pipeLoading = !!subProjectPipelinesLoading[expandKey];
                          const pipeError = !!subProjectPipelinesErrors[expandKey];
                          return (
                            <Fragment key={expandKey}>
                              <TableRow hover style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                <TableCell style={{ padding: '4px 8px', paddingLeft: 20 }}>
                                  <IconButton size="small" onClick={() => toggleSubProjectExpand(org.identifier, project.identifier)}>
                                    {isProjOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                  </IconButton>
                                </TableCell>
                                <TableCell style={{ fontWeight: 500 }}>
                                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                    <Chip label="Project" size="small" style={{ background: 'rgba(255,204,128,0.15)', color: '#ffcc80', fontSize: '0.68rem', height: 18 }} />
                                    {project.name}
                                  </Box>
                                </TableCell>
                                <TableCell style={{ fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)', fontSize: '0.82rem' }}>
                                  {project.identifier}
                                </TableCell>
                                <TableCell style={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)', fontSize: '0.82rem' }}>
                                  {org.name}{pipelines ? ` · ${pipelines.length} pipeline${pipelines.length !== 1 ? 's' : ''}` : ''}
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title={pLoading ? 'Loading…' : tracked ? 'Unsubscribe project & pipelines' : 'Subscribe project & all pipelines'}>
                                    <span>
                                      <IconButton size="small" disabled={pLoading} onClick={() => toggleSubProjectWithPipelines(org.identifier, project.identifier, project.name)}>
                                        {pLoading ? <CircularProgress size={16} /> : renderBellIcon(tracked)}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                              {isProjOpen && pipeLoading && (
                                <TableRow>
                                  <TableCell colSpan={5} style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                                    <Box display="flex" justifyContent="center" p={1}><CircularProgress size={16} /></Box>
                                  </TableCell>
                                </TableRow>
                              )}
                              {isProjOpen && !pipeLoading && pipeError && (
                                <TableRow>
                                  <TableCell colSpan={5} style={{ paddingLeft: 72, color: 'rgba(255,100,100,0.7)', fontSize: '0.82rem', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>Failed to load pipelines</TableCell>
                                </TableRow>
                              )}
                              {isProjOpen && !pipeLoading && !pipeError && pipelines && pipelines.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={5} style={{ paddingLeft: 72, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.82rem', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>No pipelines found</TableCell>
                                </TableRow>
                              )}
                              {isProjOpen && !pipeLoading && pipelines && pipelines.map(pipe => {
                                const pipeWatched = isPipelineWatched(org.identifier, project.identifier, pipe.id);
                                return (
                                  <TableRow key={pipe.id} hover style={{ background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.035)' }}>
                                    <TableCell style={{ paddingLeft: 44 }} />
                                    <TableCell style={{ fontSize: '0.82rem' }}>
                                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                        <Chip label="Pipeline" size="small" style={{ background: 'rgba(255,171,145,0.15)', color: '#ffab91', fontSize: '0.68rem', height: 18 }} />
                                        <span style={{ fontFamily: 'monospace' }}>{pipe.name}</span>
                                      </Box>
                                    </TableCell>
                                    <TableCell style={{ fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {pipe.id}
                                    </TableCell>
                                    <TableCell style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.5)', fontSize: '0.78rem' }}>
                                      {org.identifier} / {project.identifier}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Tooltip title={pipeWatched ? 'Unsubscribe' : 'Subscribe to pipeline'}>
                                        <IconButton size="small" onClick={() => togglePipelineWatch(org.identifier, project.identifier, pipe.id, pipe.name)}>
                                          {renderBellIcon(pipeWatched)}
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </Fragment>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Flat projects table */}
          {subFilterBy === 'projects' && (() => {
            const filteredProjects = allSubProjects.filter(p =>
              !subSearch ||
              p.name.toLowerCase().includes(subSearch.toLowerCase()) ||
              p.identifier.toLowerCase().includes(subSearch.toLowerCase()) ||
              p.orgName.toLowerCase().includes(subSearch.toLowerCase())
            );
            const subPerPage = 25;
            const pagedProjects = filteredProjects.slice(subProjectsPage * subPerPage, subProjectsPage * subPerPage + subPerPage);
            return (
              <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 40 }} />
                      <TableCell>Project</TableCell>
                      <TableCell>Organization</TableCell>
                      <TableCell>Identifier</TableCell>
                      <TableCell align="right">Subscribe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allSubProjectsLoading && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" style={{ padding: 40 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    )}
                    {!allSubProjectsLoading && pagedProjects.map(project => {
                      const tracked = isProjectTracked(project.orgId, project.identifier);
                      const expandKey = `${project.orgId}/${project.identifier}`;
                      const isProjOpen = subExpandedProjects.has(expandKey);
                      const pLoading = !!subProjectBellLoading[expandKey];
                      const pipelines = subProjectPipelinesCacheRef.current[expandKey];
                      const pipeLoading = !!subProjectPipelinesLoading[expandKey];
                      const pipeError = !!subProjectPipelinesErrors[expandKey];
                      return (
                        <Fragment key={expandKey}>
                          <TableRow hover>
                            <TableCell style={{ padding: '4px 8px' }}>
                              <IconButton size="small" onClick={() => toggleSubProjectExpand(project.orgId, project.identifier)}>
                                {isProjOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell style={{ fontWeight: 500 }}>
                              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Chip label="Project" size="small" style={{ background: 'rgba(255,204,128,0.15)', color: '#ffcc80', fontSize: '0.68rem', height: 18 }} />
                                {project.name}
                              </Box>
                            </TableCell>
                            <TableCell>{project.orgName}</TableCell>
                            <TableCell style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.55)' }}>{project.identifier}</TableCell>
                            <TableCell align="right">
                              <Tooltip title={pLoading ? 'Loading…' : tracked ? 'Unsubscribe project & pipelines' : 'Subscribe project & all pipelines'}>
                                <span>
                                  <IconButton size="small" disabled={pLoading} onClick={() => toggleSubProjectWithPipelines(project.orgId, project.identifier, project.name)}>
                                    {pLoading ? <CircularProgress size={16} /> : renderBellIcon(tracked)}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          {isProjOpen && pipeLoading && (
                            <TableRow>
                              <TableCell colSpan={5} style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                                <Box display="flex" justifyContent="center" p={1}><CircularProgress size={16} /></Box>
                              </TableCell>
                            </TableRow>
                          )}
                          {isProjOpen && !pipeLoading && pipeError && (
                            <TableRow>
                              <TableCell colSpan={5} style={{ paddingLeft: 64, color: 'rgba(255,100,100,0.7)', fontSize: '0.82rem', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>Failed to load pipelines</TableCell>
                            </TableRow>
                          )}
                          {isProjOpen && !pipeLoading && !pipeError && pipelines && pipelines.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} style={{ paddingLeft: 64, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.82rem', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>No pipelines found</TableCell>
                            </TableRow>
                          )}
                          {isProjOpen && !pipeLoading && pipelines && pipelines.map(pipe => {
                            const pipeWatched = isPipelineWatched(project.orgId, project.identifier, pipe.id);
                            return (
                              <TableRow key={pipe.id} hover style={{ background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.035)' }}>
                                <TableCell style={{ paddingLeft: 44 }} />
                                <TableCell style={{ fontSize: '0.82rem' }}>
                                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                    <Chip label="Pipeline" size="small" style={{ background: 'rgba(255,171,145,0.15)', color: '#ffab91', fontSize: '0.68rem', height: 18 }} />
                                    <span style={{ fontFamily: 'monospace' }}>{pipe.name}</span>
                                  </Box>
                                </TableCell>
                                <TableCell style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.5)', fontSize: '0.78rem' }}>{project.orgName}</TableCell>
                                <TableCell style={{ fontFamily: 'monospace', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pipe.id}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title={pipeWatched ? 'Unsubscribe' : 'Subscribe to pipeline'}>
                                    <IconButton size="small" onClick={() => togglePipelineWatch(project.orgId, project.identifier, pipe.id, pipe.name)}>
                                      {renderBellIcon(pipeWatched)}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                    {!allSubProjectsLoading && pagedProjects.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" style={{ padding: 32, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>No projects found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={filteredProjects.length}
                  page={subProjectsPage}
                  onPageChange={(_e, p) => setSubProjectsPage(p)}
                  rowsPerPage={subPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  onRowsPerPageChange={() => setSubProjectsPage(0)}
                />
              </TableContainer>
            );
          })()}
        </>}
      </Content>
    </Page>
  );
};
